import axios, { type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { isDeviceRegistrationInactiveMessage } from '@commerce-os/shared-types';

/** What `onUnauthorized` learns about the request that 401'd. */
export interface UnauthorizedContext {
  /**
   * The bearer that request carried (null when it sent none). Lets a caller
   * tell a USER token that needs refreshing from a DEVICE token whose
   * registration ended — the two need opposite handling.
   */
  bearer: string | null;
  /**
   * Phase 64 follow-up — the 401 is the server's refusal of a DEVICE token
   * whose registration is no longer active: its body carries
   * `DEVICE_REGISTRATION_INACTIVE_MESSAGE` (`isDeviceRegistrationInactiveError`).
   * Only this answer means "this terminal is no longer enrolled". Any other
   * 401 on a device bearer (a JWT signing-key cutover, an unverifiable
   * token) says nothing about the registration, and must not un-enroll the
   * terminal.
   */
  deviceRegistrationInactive?: boolean;
}

export interface ApiClientConfig {
  baseURL: string;
  /** Return the current access token, or null if not authenticated */
  getAccessToken?: () => string | null;
  /**
   * Called on 401 responses. Should refresh the token and resolve.
   * If it resolves, the failed request is automatically retried once.
   * If it rejects, the original 401 error propagates to the caller.
   */
  onUnauthorized?: (context: UnauthorizedContext) => Promise<void>;
  /**
   * Per-request timeout in milliseconds. Unset keeps axios's default (no timeout);
   * mobile callers pass one so a dead-but-associated network can't hang the UI.
   */
  timeout?: number;
}

/**
 * Create a typed Axios instance for the Commerce OS API.
 * Attaches JWT Bearer tokens and retries once after 401 if onUnauthorized refreshes the token.
 */
export function createApiClient(config: ApiClientConfig): AxiosInstance {
  let isRefreshing = false;
  let refreshPromise: Promise<void> | null = null;

  const client = axios.create({
    baseURL: config.baseURL,
    ...(config.timeout !== undefined ? { timeout: config.timeout } : {}),
    headers: {
      'Content-Type': 'application/json',
    },
  } satisfies AxiosRequestConfig);

  // Attach JWT token to every request
  client.interceptors.request.use((req) => {
    const token = config.getAccessToken?.();
    if (token) {
      req.headers.Authorization = `Bearer ${token}`;
    }
    return req;
  });

  // Handle 401 — refresh token and retry the failed request once
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

      // Skip the refresh-and-retry path for endpoints where 401 is a
      // legitimate credential check, not an expired session:
      //   - /auth/login + /auth/register  (user typing wrong creds)
      //   - /auth/refresh                  (refresh token itself
      //     expired; consumers handle the redirect via their own
      //     onUnauthorized fallback rather than recursing here)
      // Without this carve-out, the interceptor calls onUnauthorized
      // which (in the dashboard + pos-web) does `window.location.href
      // = '/login'`, hard-reloading the page and wiping any inline
      // error state the LoginPage just set via setError(...).
      const url = (originalRequest?.url ?? '').toLowerCase();
      const isPublicAuthEndpoint =
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/refresh') ||
        // Credential checks that answer 401 for a WRONG credential rather than
        // an expired session: a bad TOTP code, PIN, or OTP must surface as an
        // inline error on the page that asked for it, not as a session redirect.
        url.includes('/auth/mfa/validate') ||
        url.includes('/auth/pin/switch') ||
        url.includes('/auth/pin/verify') ||
        url.includes('/auth/otp/verify');

      if (
        error.response?.status === 401 &&
        config.onUnauthorized &&
        !originalRequest._retried &&
        !isPublicAuthEndpoint
      ) {
        originalRequest._retried = true;

        // Coalesce concurrent refreshes into a single call
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = config
            .onUnauthorized({
              bearer: bearerOf(originalRequest),
              deviceRegistrationInactive: isDeviceRegistrationInactiveError(error),
            })
            .finally(() => {
              isRefreshing = false;
              refreshPromise = null;
            });
        }

        await refreshPromise;
        return client(originalRequest);
      }

      return Promise.reject(error);
    },
  );

  return client;
}

function bearerOf(req: InternalAxiosRequestConfig | undefined): string | null {
  const header = req?.headers?.Authorization ?? req?.headers?.authorization;
  if (typeof header !== 'string') return null;
  const match = /^Bearer\s+(.+)$/i.exec(header.trim());
  return match ? match[1] : null;
}

/**
 * True for the server's refusal of a device token whose registration is no
 * longer active (revoked, decommissioned or re-provisioned) — an HTTP 401
 * carrying `DEVICE_REGISTRATION_INACTIVE_MESSAGE`. Credential-check
 * endpoints (`/auth/pin/switch`, …) skip `onUnauthorized`, so their callers
 * test the error with this.
 */
export function isDeviceRegistrationInactiveError(error: unknown): boolean {
  const res = (error as { response?: { status?: number; data?: { message?: unknown } } })?.response;
  return res?.status === 401 && isDeviceRegistrationInactiveMessage(res.data?.message);
}

export { type AxiosInstance } from 'axios';

// ── Typed endpoint wrappers ─────────────────────────────────────────────
export * as posApi from './pos';
export * as taxApi from './tax';
export * as terminalApi from './terminal';
export * as kdsApi from './kds';
export * as payrollApi from './payroll';
export * as storefrontApi from './storefront';
export * as deliveryApi from './delivery';
// Phase 65.2 — shipping setup on the merchant's own EasyPost account.
export * as shippingApi from './shipping';
export * as inventoryApi from './inventory';
export * as loyaltyApi from './loyalty';
export * as houseAccountsApi from './houseAccounts';
export * as reservationApi from './reservation';
export * as waitlistApi from './waitlist';
export * as messagingApi from './messaging';
export * as publicBookingApi from './public-booking';
// Phase 55.1 — customer portal hub.
export * as customerPortalApi from './customer-portal';
export * as recipeApi from './recipe';
export * as marketingApi from './marketing';
export * as authApi from './auth';
export * as platformApi from './platform';
export * as analyticsApi from './analytics';
export * as integrationsApi from './integrations';
export * as migrationApi from './migration';
export * as ssoApi from './sso';
export * as rolesApi from './roles';
export * as telemetryApi from './telemetry';
export * as auditApi from './audit';
export * as helpApi from './help';
export * as helpAnalyticsApi from './help-analytics';
export * as supportApi from './support';
export * as billingApi from './billing';
export * as pushApi from './push-tokens';
export * as engagementApi from './engagement';
export * as textAlertsApi from './text-alerts';
// Phase 68.3 — guests' yes to a business's texts, and its texting terms.
export * as guestTextingApi from './guest-texting';
// Phase 68.4 — a business registering to text its guests.
export * as smsRegistrationApi from './sms-registration';
export * as pilotsApi from './pilots';
export * as costCheckApi from './costCheck';
export * as menuVersionsApi from './menuVersions';
export * as locationGroupsApi from './locationGroups';
export * as franchiseApi from './franchise';
// Phase 47.2 — RN POS runtime device enrollment.
export * as devicesApi from './devices';
// Phase 50 — field service management.
export * as fieldServiceApi from './field-service';
// Phase 64 — online-order fulfillment settings + scheduled-order fire.
export * as onlineFulfillmentApi from './online-fulfillment';

// Re-export pagination / query shapes that consumers need to type their own
// wrapper functions. Kept flat at the top level so app code doesn't have to
// reach into namespaced members for a type.
export type {
  ListRegistersQuery,
  RegistersListResponse,
  GiftCardsListResponse,
  GiftCardTransactionsResponse,
  BarTabListItem,
  BarTabDetail,
  BarTabOrderSummary,
} from './pos';
